import { useToastStore } from '@/store/useToastStore';
import { cn } from '@/lib/cn';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import type { ToastMessage } from '@/types';

const iconByType: Record<ToastMessage['type'], typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const styleByType: Record<ToastMessage['type'], string> = {
  success: 'border-success bg-success-bg text-success-dark',
  error: 'border-danger bg-danger-bg text-danger-dark',
  warning: 'border-warning bg-warning-bg text-warning-dark',
  info: 'border-info bg-info-bg text-info-dark',
};

export function ToastViewport() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[400] flex flex-col gap-2 max-w-sm pointer-events-none"
    >
      {toasts.map((t) => {
        const Icon = iconByType[t.type];
        return (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-md shadow-md border-l-4',
              'bg-white border border-line animate-fade-in-up',
              styleByType[t.type],
            )}
            role="alert"
          >
            <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-ink-primary">{t.message}</div>
            {t.action && (
              <button
                type="button"
                onClick={() => {
                  t.action!.onClick();
                  dismiss(t.id);
                }}
                className="text-sm font-semibold text-brand-orange hover:text-brand-orange-light hover:underline transition-colors flex-shrink-0"
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="text-ink-tertiary hover:text-ink-primary transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
